import {
	centerOfMass,
	area,
	kinks,
	point,
	polygon,
	coordAll,
	booleanPointInPolygon
} from '@turf/turf';
import config from 'config';

export const commodityColors = {
	42: 'silver', // al
	1: 'orange', // cu
	4: 'brown', // fe
	9: 'purple', // fecr
	2: 'green', // ni
	7: 'gray', // scrap_steel
	3: '#efcc00', // steel
	6: 'teal', // ti
	8: 'blue', // zn
	5: 'black' // airfield/blank
};

// const mapboxDrawOutputFormat = [
//     {
//         "id": "mDxi78ssS6YJeKNVPwwntx4ktAgW4I18",
//         "type": "Feature",
//         "properties": {},
//         "geometry": {
//             "coordinates": [
//                 [
//                     [
//                         -1.637452898806373,
//                         38.623713807855324
//                     ],
//                     [
//                         -1.6373756958637387,
//                         38.62438481732747
//                     ],
//                     [
//                         -1.635435971922277,
//                         38.624407435515536
//                     ],
//                     [
//                         -1.6355614267041858,
//                         38.62364595261502
//                     ],
//                     [
//                         -1.6369414293044713,
//                         38.623321754469686
//                     ],
//                     [
//                         -1.637452898806373,
//                         38.623713807855324
//                     ]
//                 ]
//             ],
//             "type": "Polygon"
//         }
//     }
// ]

export const commodityColorExpression = [
	'match',
	['get', 'commodity'],
	...Object.entries(commodityColors).flatMap(([id, color]) => [
		Number(id),
		color
	]),
	'pink'
];

export const getMapSiteLayers2 = (sites, siteTypes) => {
	const mapboxJSONs = {
		smelter: '',
		airfield: '',
		stockpiles: ''
	};

	siteTypes.forEach(({ id, name }) => {
		const features = sites
			.filter(
				(site) =>
					site.site_type_id === id && site.lat != null && site.lon != null
			)
			.map(mapLayerPointFormat2);

		mapboxJSONs[name] = {
			type: 'geojson',
			data: {
				type: 'FeatureCollection',
				features
			}
		};
	});

	return mapboxJSONs;
};

export const mapLayerPointFormat2 = (site) => ({
	type: 'Feature',
	properties: {
		id: site.id,
		name: site.name,
		commodity: site.commodity_id
	},
	geometry: {
		type: 'Point',
		coordinates: [site.lon, site.lat]
	}
});

export const getDrawnAreaDetails = async (
	geojson,
	parentSite,
	parentAOI,
	countries,
	regions
) => {
	console.log(geojson)

	let info = {};
	let error = ''
	const poly = polygon(geojson.coordinates);

	const lat = centerOfMass(poly)['geometry']['coordinates'][1];
	const long = centerOfMass(poly)['geometry']['coordinates'][0];
	const areaAOI = area(poly) / 1000000;
	const result = await getCountryAndRegion2(lat, long, countries, regions);

	let aoiValid = await validateParentAOI(geojson, areaAOI);

	if (!aoiValid) {
		error = 'Drawn AOIs must be a valid shape (no intersecting lines) and less than 10km2.'
	}

	if (parentSite) {
		aoiValid = await validateSubsiteAOI(geojson, parentAOI);
	}

	if (!aoiValid) {
		error =  "Subsite AOIs must be drawn within the parent AOI, be of a valid shape (no intersecting lines) and be less than 10km2."
	}

	if (error.length > 0) {
		return {
			error: error
		}
	} 

	info = {
		center: [long, lat],
		area: areaAOI,
		country: result && result[0],
		region: result && result[1],
	};

	return info;
};

export const getCountryAndRegion2 = async (lat, long, countries, regions) => {
	try {
		const response = await fetch(
			`https://api.mapbox.com/geocoding/v5/mapbox.places/${long},${lat}.json?types=country&access_token=${config.mapboxToken}`
		);
		const data = await response.json();

		if (!data.features || data.features.length === 0) {
			throw new Error('No location data returned from Mapbox.');
		}

		console.log(data)

		const country_code = data.features[0]?.properties?.short_code;
		const country_name = data.features[0].place_name
		const countryMatch = countries.find((c) => c.iso_code === country_code.toUpperCase());

		if (!countryMatch) {
			throw new Error(`Country "${country_name}" not available in database`);
		}

		const regionMatch = regions.find((r) => r.name === countryMatch.region);

		if (!regionMatch) {
			throw new Error(`Region for country "${country_name}" not found.`);
		}

		return [countryMatch.id, regionMatch.id];
	} catch (error) {
		console.log(error);
	}
};

export const validateParentAOI = async (geojson, area) => {
	let valid = true;
	const poly = polygon(geojson.coordinates);

	if (kinks(poly).features.length > 0) {
		valid = false;
	}

	if (area > 100) {
		valid = false;
	}

	return valid;
};

export const validateSubsiteAOI = async (geojson, parentAOI) => {
	if (!geojson || !parentAOI) return false;

	const coordinates = coordAll(geojson);

	return coordinates.every((coord) =>
		booleanPointInPolygon(point(coord), parentAOI)
	);
};

export const toFeatureCollection = (geojson) => {
	const featureCollection = {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				properties: {},
				geometry: {
					type: 'Polygon',
					coordinates: geojson.coordinates
				}
			}
		]
	};
	return featureCollection;
};

export const openInGeojsonIO = (geojson) => {
	const feature = toFeatureCollection(geojson);
	let encodedJSON = encodeURIComponent(JSON.stringify(feature));

	let url = 'http://geojson.io/#data=data:application/json,';
	window.open(`${url}${encodedJSON}`);
};

export const copyAOI = (geojson) => {
	const feature = toFeatureCollection(geojson);
	navigator.clipboard.writeText(JSON.stringify(feature));
};

function escapeXml(value) {
	return value
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&apos;');
}

export function downloadGeojson({ geometry, site }) {
	let fileName = '';
	if (site) {
		fileName = `${site.id}-${site.name}`;
	} else {
		fileName = 'unnamed-site';
	}

	console.log(geometry)
	const feature = toFeatureCollection(geometry);

	const blob = new Blob([JSON.stringify(feature, null, 2)], {
		type: 'application/geo+json'
	});

	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');

	link.href = url;
	link.download = `${fileName}.geojson`;
	link.click();

	URL.revokeObjectURL(url);
}

export function downloadKML({ geometry, site }) {
	let fileName = '';
	if (site) {
		fileName = `${site.id}-${site.name}`;
	} else {
		fileName = 'unnamed-site';
	}

	const coordinates = geometry.coordinates[0]
		.map(([lng, lat]) => `${lng},${lat},0`)
		.join(' ');

	const safeFileName = escapeXml(fileName);

	const kml = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
    <Document>
        <name>${safeFileName}</name>
        <Placemark>
            <name>${safeFileName}</name>
            <Polygon>
                <outerBoundaryIs>
                    <LinearRing>
                        <coordinates>${coordinates}</coordinates>
                    </LinearRing>
                </outerBoundaryIs>
            </Polygon>
        </Placemark>
    </Document>
</kml>`;

	const blob = new Blob([kml], {
		type: 'application/vnd.google-earth.kml+xml'
	});

	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');

	link.href = url;
	link.download = `${fileName}.kml`;
	link.click();

	URL.revokeObjectURL(url);
}

export const createCSVDownloadOfSites = (sites, commodities, siteTypes) => {
	var currentdate = new Date();
	var datetime =
		currentdate.getDate() +
		'/' +
		(currentdate.getMonth() + 1) +
		'/' +
		currentdate.getFullYear();

	// unpack 'params' object in sites object
	let csv_sites = [];

	if (sites.length > 0 && commodities.length > 0 && siteTypes.length > 0) {
		for (let i in sites) {
			let csv_site = {};

			if (sites[i].hasOwnProperty('params')) {
				for (let j in Object.keys(sites[i]['params'])) {
					csv_site[Object.keys(sites[i]['params'])[j]] =
						sites[i]['params'][Object.keys(sites[i]['params'])[j]];
				}
			}

			for (let k in Object.keys(sites[i])) {
				if (Object.keys(sites[i])[k] !== 'params') {
					if (Object.keys(sites[i])[k] === 'commodity_id') {
						csv_site[Object.keys(sites[i])[k]] = commodities.filter(
							({ id }) => id === sites[i][Object.keys(sites[i])[k]]
						)[0]['symbol'];
					} else if (Object.keys(sites[i])[k] === 'site_type_id') {
						csv_site[Object.keys(sites[i])[k]] = siteTypes.filter(
							({ id }) => id === sites[i][Object.keys(sites[i])[k]]
						)[0]['name'];
					} else {
						csv_site[Object.keys(sites[i])[k]] =
							sites[i][Object.keys(sites[i])[k]];
					}
				}
			}

			csv_sites.push(csv_site);
		}
	}

	return [datetime, csv_sites];
};

export const changeChevron = (event, ind) => {
	if (event.target.classList.contains('fa-caret-down')) {
		document.getElementById(`id${ind}`).classList.remove('fa-caret-down');
		document.getElementById(`id${ind}`).classList.add('fa-caret-up');
	} else {
		document.getElementById(`id${ind}`).classList.remove('fa-caret-up');
		document.getElementById(`id${ind}`).classList.add('fa-caret-down');
	}
};
